<script setup lang="ts">
  import { computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { Loader2 } from "lucide-vue-next";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
  import { Button } from "@/components/ui/button";
  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import { useJcdCampaignsStore } from "@stores/jcdCampaigns";

  const props = defineProps<{
    open: boolean;
  }>();

  const emit = defineEmits<{
    "update:open": [value: boolean];
  }>();

  const store = useJcdCampaignsStore();
  const { referentials, yearFilter, weekFilter } = storeToRefs(store);

  const year = ref<number | null>(null);
  const week = ref<number | null>(null);
  const isSubmitting = ref(false);

  watch(
    () => props.open,
    (open) => {
      if (!open) return;
      year.value = yearFilter.value.length === 1 ? Number(yearFilter.value[0]) : null;
      week.value = weekFilter.value.length === 1 ? Number(weekFilter.value[0]) : null;
    },
  );

  const yearOptions = computed(() =>
    Object.keys(referentials.value?.weeks_by_year || {}).map((value) => ({ label: value, value: Number(value) })),
  );
  const weeksCount = computed(() => (year.value && referentials.value?.weeks_by_year[year.value]) || 53);
  const weekOptions = computed(() =>
    Array.from({ length: weeksCount.value }, (_, index) => ({ label: `S${index + 1}`, value: index + 1 })),
  );

  watch(year, () => {
    if (week.value && week.value > weeksCount.value) week.value = null;
  });

  const handleSubmit = async () => {
    if (!year.value || !week.value) return;
    isSubmitting.value = true;
    const started = await store.generateSurvey(year.value, week.value);
    isSubmitting.value = false;
    if (started) emit("update:open", false);
  };
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)">
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Nouveau questionnaire</DialogTitle>
        <DialogDescription>
          Génère ou actualise le questionnaire de la semaine à partir du template. Les campagnes non prêtes (⚠️)
          seront ignorées — relancer la génération les ajoutera une fois complétées.
        </DialogDescription>
      </DialogHeader>
      <div class="grid grid-cols-2 gap-4">
        <div class="space-y-2">
          <Label class="text-sm">Année</Label>
          <Select
            v-model="year"
            :options="yearOptions"
            placeholder="Année" />
        </div>
        <div class="space-y-2">
          <Label class="text-sm">Semaine</Label>
          <Select
            v-model="week"
            :options="weekOptions"
            filterable
            placeholder="Semaine" />
        </div>
      </div>
      <DialogFooter>
        <Button
          :disabled="!year || !week || isSubmitting"
          @click="handleSubmit">
          <Loader2
            v-if="isSubmitting"
            class="mr-2 h-4 w-4 animate-spin" />
          <AiStar
            v-else
            class="mr-2 h-4 w-4" />
          Générer
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
